/**
 * plannedPoints — geplande boorlocaties uit een meetstaat, CSV of tekening.
 *
 * Een gepland punt is GEEN boring. Het is de plek waar de projectleider een boring wil hebben;
 * de boring zelf ontstaat pas in het veld. Daarom een eigen tabel en niet een boring met een
 * status 'gepland': dan zou elke export, telling en sync die boringen leest moeten weten dat hij
 * er een deel van moet overslaan, en één vergeten filter zet een lege boring in een XML.
 *
 * Gekoppeld wordt op NAAM (hoofdletters en spaties genegeerd), net als het koppelen van een
 * ingesproken boring aan een bestaande. Een punt 'B01' is uitgevoerd zodra er een actieve
 * boring 'b01' in het project staat.
 *
 * Coördinaten worden, zoals overal, als WGS84 opgeslagen (x = lengtegraad, y = breedtegraad).
 * Wat er in het bestand stond (RD, UTM, graden) bepaalt alleen de omrekening bij het inlezen.
 */
import { convertToWgs84, type CoordinateSystem } from './index.js';

export type PlannedPointKind = 'boring' | 'peilbuis' | 'proefgat' | 'sleuf' | 'overig';

export const PLANNED_POINT_KINDS: { id: PlannedPointKind; label: string }[] = [
  { id: 'boring', label: 'Boring' },
  { id: 'peilbuis', label: 'Peilbuis' },
  { id: 'proefgat', label: 'Proefgat' },
  { id: 'sleuf', label: 'Sleuf' },
  { id: 'overig', label: 'Overig' },
];

export interface PlannedPoint {
  id: string;
  projectId: string;
  name: string;
  kind: PlannedPointKind;
  /** WGS84 lengtegraad. */
  x: number;
  /** WGS84 breedtegraad. */
  y: number;
  /** Ontwerpdiepte in cm onder maaiveld, als het bestand die gaf. */
  plannedDepthCm: number | null;
  note: string | null;
  /** Kaartlaag (tekening) waar het punt uit kwam, of null bij een losse CSV/handmatig punt. */
  sourceLayerId: string | null;
  createdAt?: string | null;
  deletedAt?: string | null;
}

/** Naam zoals we hem vergelijken: geen hoofdletters, geen dubbele of rand-spaties. */
export function normalizePointName(name: unknown): string {
  return String(name ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Soort afleiden uit de naam, voor als het bestand geen soortkolom heeft. Bewust voorzichtig:
 * alleen gangbare voorvoegsels; alles wat twijfel geeft wordt een boring.
 */
export function guessKindFromName(name: unknown): PlannedPointKind {
  const n = normalizePointName(name);
  if (/^(pb|peilbuis)\s*-?\d/.test(n) || n.startsWith('peilbuis')) return 'peilbuis';
  if (/^(pg|proefgat|gat|ig|inspectiegat)\s*-?\d/.test(n) || n.startsWith('proefgat')) return 'proefgat';
  if (/^(ps|sl|sleuf|proefsleuf)\s*-?\d/.test(n) || n.startsWith('sleuf') || n.startsWith('proefsleuf')) return 'sleuf';
  return 'boring';
}

export function normalizeKind(kind: unknown, name?: unknown): PlannedPointKind {
  const k = String(kind ?? '').trim().toLowerCase();
  if (PLANNED_POINT_KINDS.some((d) => d.id === k)) return k as PlannedPointKind;
  if (k.startsWith('pb') || k.startsWith('peil')) return 'peilbuis';
  if (k.startsWith('proefg') || k === 'pg' || k.startsWith('gat') || k.startsWith('inspectieg')) return 'proefgat';
  if (k.includes('sleuf')) return 'sleuf';
  if (k.startsWith('bor') || k === 'b') return 'boring';
  return guessKindFromName(name);
}

// ── Coördinaatsysteem herkennen ─────────────────────────────────────────────

/** Ruime grenzen per stelsel, rond Nederland. Een meetstaat zegt zelden welk stelsel hij gebruikt. */
const RANGES: { system: CoordinateSystem; x: [number, number]; y: [number, number] }[] = [
  { system: 'RD', x: [-7000, 300000], y: [289000, 629000] },
  { system: 'ETRS89_UTM31N', x: [400000, 900000], y: [5500000, 6100000] },
  { system: 'WGS84', x: [2.5, 7.5], y: [50.5, 53.8] },
];

export interface CoordinateGuess {
  system: CoordinateSystem;
  /** X en Y staan andersom in het bestand (Y eerst). */
  swapped: boolean;
  /** Aandeel van de punten dat binnen de grenzen viel (0–1). */
  confidence: number;
}

/**
 * Raad het stelsel van een reeks (x, y)-paren. Probeert ook verwisselde assen: een lat/lon-
 * export zet de breedtegraad vaak eerst. Geeft null als niets overtuigend past -- dan kiest de
 * gebruiker zelf, liever dan dat we punten stil in de Noordzee leggen.
 */
export function guessCoordinateSystem(pairs: [number, number][]): CoordinateGuess | null {
  const valid = pairs.filter(([a, b]) => Number.isFinite(a) && Number.isFinite(b));
  if (!valid.length) return null;
  let best: CoordinateGuess | null = null;
  for (const r of RANGES) {
    for (const swapped of [false, true]) {
      const inside = valid.filter(([a, b]) => {
        const [x, y] = swapped ? [b, a] : [a, b];
        return x >= r.x[0] && x <= r.x[1] && y >= r.y[0] && y <= r.y[1];
      }).length;
      const confidence = inside / valid.length;
      if (!best || confidence > best.confidence) best = { system: r.system, swapped, confidence };
    }
  }
  return best && best.confidence >= 0.5 ? best : null;
}

/** Eén punt uit het bestand → WGS84, of null als het niet om te rekenen is. */
export function pointToWgs84(
  x: number, y: number, system: CoordinateSystem, swapped = false,
): { x: number; y: number } | null {
  const [a, b] = swapped ? [y, x] : [x, y];
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  const r = convertToWgs84(a, b, system);
  if (!r || Math.abs(r.longitude) > 180 || Math.abs(r.latitude) > 90) return null;
  return { x: r.longitude, y: r.latitude };
}

/**
 * Getal uit een cel: accepteert de Nederlandse komma ('155000,25') en duizendtallen met punten
 * ('155.000,25'), en ook de Engelse notatie. Geeft NaN als het geen getal is.
 */
export function parseNumber(raw: unknown): number {
  if (typeof raw === 'number') return raw;
  let s = String(raw ?? '').trim().replace(/\s/g, '');
  if (!s) return NaN;
  const hasComma = s.includes(',');
  const hasDot = s.includes('.');
  if (hasComma && hasDot) {
    // Wat het laatst staat is het decimaalteken.
    s = s.lastIndexOf(',') > s.lastIndexOf('.') ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '');
  } else if (hasComma) {
    s = s.replace(',', '.');
  }
  return /^[-+]?\d*\.?\d+(e[-+]?\d+)?$/i.test(s) ? Number(s) : NaN;
}

/**
 * Diepte uit een cel → cm. '2' of '2,0' of '2 m' = meters, '200 cm' = cm. Een kaal getal boven
 * de 30 wordt als cm gelezen: een handboring van 30 meter bestaat niet, 50 cm wel.
 */
export function parseDepthCm(raw: unknown): number | null {
  const s = String(raw ?? '').trim().toLowerCase();
  if (!s) return null;
  const n = parseNumber(s.replace(/m-?mv|cm|m/g, '').trim());
  if (!Number.isFinite(n) || n <= 0) return null;
  if (/cm/.test(s)) return Math.round(n);
  if (/m/.test(s)) return Math.round(n * 100);
  return n > 30 ? Math.round(n) : Math.round(n * 100);
}

// ── Status: welke punten zijn al geboord ────────────────────────────────────

export interface BoringRef { id: string; name: string; deletedAt?: string | null; plannedPointId?: string | null }

/**
 * Koppel geplande punten aan bestaande boringen. Eerst de expliciete koppeling: een boring die
 * zegt welk punt hij uitvoert (plannedPointId, gekozen in de veldapp). Daarna op naam, voor de
 * punten en boringen die nog vrij zijn -- zo blijft 'boring B03 ingesproken bij punt B03' werken
 * zonder dat iemand iets aanwijst. Verwijderde boringen tellen niet: een weggegooide boring maakt
 * het punt weer open.
 */
export function matchPlannedPoints<P extends { id: string; name: string }>(
  points: P[], borings: BoringRef[],
): Map<string, BoringRef> {
  const out = new Map<string, BoringRef>();
  const pointIds = new Set(points.map((p) => p.id));
  const linked = new Set<string>();
  for (const b of borings) {
    if (b.deletedAt || !b.plannedPointId || !pointIds.has(b.plannedPointId) || out.has(b.plannedPointId)) continue;
    out.set(b.plannedPointId, b);
    linked.add(b.id);
  }
  const byName = new Map<string, BoringRef>();
  for (const b of borings) {
    // Een boring die aan een (ander) punt hangt, telt niet ook nog eens op naam.
    if (b.deletedAt || linked.has(b.id) || (b.plannedPointId && pointIds.has(b.plannedPointId))) continue;
    const key = normalizePointName(b.name);
    if (key && !byName.has(key)) byName.set(key, b);
  }
  for (const p of points) {
    if (out.has(p.id)) continue;
    const b = byName.get(normalizePointName(p.name));
    if (b) { out.set(p.id, b); byName.delete(normalizePointName(p.name)); }
  }
  return out;
}

// ── Afstand en richting (veldapp: 'nog 12 m naar het noordoosten') ─────────

/** Afstand in meters tussen twee WGS84-punten (haversine; ruim nauwkeurig genoeg op een terrein). */
export function distanceMeters(lon1: number, lat1: number, lon2: number, lat2: number): number {
  const R = 6371008.8;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** Kompasrichting (0 = noord, 90 = oost) van punt 1 naar punt 2. */
export function bearingDegrees(lon1: number, lat1: number, lon2: number, lat2: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const y = Math.sin(toRad(lon2 - lon1)) * Math.cos(toRad(lat2));
  const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(toRad(lon2 - lon1));
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

const COMPASS = ['noord', 'noordoost', 'oost', 'zuidoost', 'zuid', 'zuidwest', 'west', 'noordwest'];

export function compassLabel(bearing: number): string {
  return COMPASS[Math.round((((bearing % 360) + 360) % 360) / 45) % 8];
}

// ── Deellocatie van een gepland punt ──────────────────────────────────────

/** Ligt [lng, lat] binnen de ring? Straalmethode, in graden (nauwkeurig genoeg op een terrein). */
function inRing(lng: number, lat: number, ring: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/**
 * In welke deellocatie ligt een gepland punt? Zelfde regel als het monsterplan in de backoffice:
 * eerst de notitie die het monsterplan schrijft ('Monsterplan DL02 · VEP' → DL02; bij
 * overlappende vlakken hoort een punt bij één), anders de ligging als het punt in precies één
 * deellocatie valt. Anders null: liever geen deellocatie dan een gok.
 */
export function sublocationOfPlannedPoint(
  point: { x: number; y: number; note?: string | null },
  sublocations: { id: string; name: string; geometry?: [number, number][] | null }[],
): string | null {
  const note = String(point.note || '').trim();
  const m = /^Monsterplan\s+(.+?)(?:\s+·.*)?$/.exec(note);
  if (m) {
    const s = sublocations.find((x) => String(x.name).trim() === m[1].trim());
    if (s) return s.id;
  }
  const binnen = sublocations.filter((s) => (s.geometry || []).length >= 3 && inRing(point.x, point.y, s.geometry as [number, number][]));
  return binnen.length === 1 ? binnen[0].id : null;
}
